/**
 * @module cercaliagl/control/FullScreen
*/
import maplibregl from 'maplibre-gl';
import Control from './Control';
import MapControls from './MapControls';
import { i18n } from '../dictionary';
import VControlButton from '../view/VControlButton';

/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const control = new cercaliagl.control.FullScreem();
 * map.addControl(control);
 * ```
 * <br/>
 * Class <b/>cercaliagl.control.FullScreen<b> is used for internal purposes, but can be accessed throught methods.
 *
 * Usage getting full screen control from map:
 *
 * ```js
 * const map = new cercaliagl.Map({ target: 'map' });
 * const fullScreen = map.getControlByName(cercaliagl.control.MapControls.FULLSCREEN);
 * fullScreen.isEnable()
 * ```
 *
 * @extends {Control}
 * @api
 */
class FullScreen extends Control {
  /**
   * @constructor
   */
  constructor() {
    const controlButton = new VControlButton('fullscreen');
    const element = controlButton.getElement();
    super({
      element,
      name: MapControls.FULLSCREEN,
      className: 'cercaliagl.control.FullScreen',
    });

    /**
     * @private
     * @type {VControlButton}
     */
    this.controlButton_ = controlButton;

    /**
     * @private
     * @type {maplibregl.FullscreenControl}
     */
    this.fullScreenControl_ = new maplibregl.FullscreenControl({ onAdd: this.onAdd });

    /**
     * @private
     * @type {HTMLElement}
     */
    this.element_ = element;

    /**
     * @private
     * @type {boolean}
     */
    this.enabled = false;

    this.initialize_();
  }

  /**
   * @override
   * @param {import("maplibre-gl").Map} map Mapbox map
   */
  onAdd(map) {
    this.map_ = map;
    this.fullScreenControl_.onAdd(map);
    return this.element_;
  }

  /**
   * @private
   */
  initialize_() {
    this.controlButton_.setTooltip(i18n('fullScreen'));
    this.element_.addEventListener('click', this.handleClick_.bind(this));
  }

  /**
   * @private
   */
  handleClick_() {
    this.fullScreenControl_._onClickFullscreen();
    this.enabled = !this.enabled;
  }

  /**
  * @return {boolean} Is fullscreen enabled
   * @api
   */
  isEnabled() {
    return this.enabled;
  }

  /**
   * Toggle fullscreen
   * @api
   */
  toggle() {
    this.handleClick_();
  }
}

export default FullScreen;
